import { useEffect, useRef, useState } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";
import { LANGUAGES } from "@/i18n/languages";
import { useI18n } from "@/i18n/I18nProvider";

export const LanguagePicker = () => {
  const { lang, setLang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGUAGES.find((l) => l.code === lang);

  // Deps: ref is stable and setOpen is a state setter; `open` is the only reactive value.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => !ref.current?.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); window.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label={t("Change language")} data-testid="lang-toggle"
        className="inline-flex items-center gap-1.5 h-11 px-3 rounded-md border-2 border-slate-200 text-sm font-semibold text-[var(--ink)] hover:border-[var(--blue)] hover:text-[var(--blue)] transition-colors whitespace-nowrap">
        <Globe size={17} className="text-[var(--blue)]" />
        <span className="hidden sm:inline 2xl:hidden" lang={lang}>{current.native}</span>
        <span className="sm:hidden 2xl:inline uppercase">{lang.split("-")[0]}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul role="listbox" aria-label={t("Language")} data-testid="lang-menu"
          className="absolute end-0 mt-2 w-64 max-h-[70vh] overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-2xl shadow-slate-900/15 p-1.5 z-[70]">
          {LANGUAGES.map((l) => {
            const active = l.code === lang;
            return (
              <li key={l.code} role="option" aria-selected={active}>
                <button type="button" lang={l.code} onClick={() => { setLang(l.code); setOpen(false); }} data-testid={`lang-option-${l.code}`}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-start text-sm transition-colors ${active ? "bg-[var(--blue)] text-white" : "hover:bg-[var(--blue-tint)] text-[var(--ink)]"}`}>
                  <span className="font-semibold">{l.native}</span>
                  <span className={`text-xs ${active ? "text-white/80" : "text-[var(--muted)]"}`}>{l.name}</span>
                  {active && <Check size={16} />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
